(() => {
  'use strict';
  if (customElements.get('kozart-personalizer')) return;
  const E = window.KozartPersonalizerImage;
  const styles = ['classic', 'cloud', 'sketch'];
  const textDesigns = ['elegant', 'handwritten'];
  const defaults = {style: 'classic', zoom: 1, fx: .5, fy: .5, text: '', textDesign: 'elegant', textSize: 1, textPosition: 'bottom'};
  const clamp = (v, a, b) => Math.max(a, Math.min(b, v));
  const uid = () => typeof crypto.randomUUID === 'function' ? crypto.randomUUID() : Date.now().toString(36) + Math.random().toString(36).slice(2);
  const round = v => Math.round(v * 1e6) / 1e6;
  let dbPromise;
  function database() {
    if (!dbPromise) dbPromise = new Promise((resolve, reject) => {
      const request = indexedDB.open('kozart-personalizer-v1', 1);
      request.onupgradeneeded = () => request.result.createObjectStore('drafts', {keyPath: 'id'});
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
      request.onblocked = () => reject(new Error('Storage unavailable'));
    });
    return dbPromise;
  }
  async function storeFile(id, file) {
    const db = await database();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction('drafts', 'readwrite'), store = transaction.objectStore('drafts');
      if (file) store.put({id, file, saved: Date.now()}); else store.delete(id);
      const cursor = store.openCursor();
      cursor.onsuccess = () => { const row = cursor.result; if (row) { if (row.value.saved < Date.now() - 86400000) row.delete(); row.continue(); } };
      transaction.oncomplete = resolve; transaction.onerror = () => reject(transaction.error);
    });
  }
  async function getFile(id) {
    const db = await database();
    return new Promise((resolve, reject) => {
      const request = db.transaction('drafts', 'readonly').objectStore('drafts').get(id);
      request.onsuccess = () => resolve(request.result?.saved > Date.now() - 86400000 ? request.result.file : null);
      request.onerror = () => reject(request.error);
    });
  }
  class KozartPersonalizer extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.form = document.getElementById(this.dataset.formId);
      if (!this.form) { this.initialized = false; return; }
      this.scope = this.closest('.shopify-section') || this.form.parentElement;
      this.config = JSON.parse(this.querySelector('[data-config]').textContent);
      this.t = this.config.strings;
      this.state = {...defaults}; this.revision = 1; this.fileVersion = 0; this.loadVersion = 0;
      this.preview = this.querySelector('[data-preview]'); this.input = this.querySelector('[data-file]');
      this.textarea = this.querySelector('[data-text]'); this.zoom = this.querySelector('[data-zoom]');
      this.errorBox = this.querySelector('[data-error]'); this.status = this.querySelector('[data-status]');
      this.buttons = [...this.querySelectorAll('[data-kadp-submit]')];
      this.listeners = new AbortController();
      this.storageKey = 'KADPersonalizer:v1:' + this.dataset.productId;
      let sessionId = uid();
      try {
        sessionId = sessionStorage.getItem('KADPersonalizer:tab') || sessionId;
        sessionStorage.setItem('KADPersonalizer:tab', sessionId);
        const saved = JSON.parse(sessionStorage.getItem(this.storageKey) || 'null');
        if (saved?.state) { this.restoreState(saved.state); this.pending = saved.pending; }
      } catch {}
      this.fileKey = sessionId + ':' + this.dataset.productId;
      if (!E) { this.showError(this.t.upload_error); return; }
      this.form.enctype = 'multipart/form-data';
      // Keep the standard Shopify form visible until all editor controls initialize.
      this.bind(); this.syncControls(); this.renderBlank(); this.renderTextCards(); this.updateVariant();
      this.form.dataset.kadpReady = 'true';
      this.loadDemo();
      // The original can be restored from IndexedDB without populating a file input.
      this.input.required = false;
      if (this.state.text.trim()) this.querySelector('.kadp__optional-text').open = true;
      this.suppressLegacy();
      this.legacyObserver = new MutationObserver(() => this.suppressLegacy());
      this.legacyObserver.observe(this.scope, {childList: true, subtree: true});
      getFile(this.fileKey).then(file => { if (file && !this.file && !this.loading) return this.loadFile(file, true); }).catch(() => {});
      if (document.fonts) {
        document.fonts.load('24px "KAD Caveat"').then(() => { if (this.isConnected) { this.renderTextCards(); this.scheduleRender(); } }).catch(() => {});
        document.fonts.ready.then(() => { if (this.isConnected) this.scheduleRender(); });
      }
    }
    disconnectedCallback() {
      this.listeners?.abort(); this.legacyObserver?.disconnect();
      clearTimeout(this.thumbnailTimer); clearTimeout(this.saveTimer); clearTimeout(this.detailTimer);
      cancelAnimationFrame(this.renderFrame); this.initialized = false;
    }
    restoreState(s) {
      this.state = {
        style: styles.includes(s.style) ? s.style : defaults.style,
        zoom: clamp(Number(s.zoom) || 1, 1, 2.5), fx: clamp(Number(s.fx) || .5, 0, 1), fy: clamp(Number(s.fy) || .5, 0, 1),
        text: String(s.text || '').slice(0, 60), textDesign: textDesigns.includes(s.textDesign) ? s.textDesign : 'elegant',
        textSize: [.8, 1, 1.2].includes(Number(s.textSize)) ? Number(s.textSize) : 1,
        textPosition: ['top', 'bottom'].includes(s.textPosition) ? s.textPosition : 'bottom'
      };
    }
    listen(target, type, callback, extra = {}) { target.addEventListener(type, callback, {...extra, signal: this.listeners.signal}); }
    bind() {
      this.listen(this.input, 'change', event => { event.stopPropagation(); if (this.input.files[0]) this.loadFile(this.input.files[0], false); });
      this.listen(this, 'click', event => {
        const button = event.target.closest('button');
        if (!button || this.busy) return;
        if (button.hasAttribute('data-kadp-submit')) {
          event.preventDefault(); event.stopImmediatePropagation();
          this.submit(); return;
        }
        if (button.dataset.style) { this.state.style = button.dataset.style; this.changed(); }
        if (button.dataset.textDesign) { this.state.textDesign = button.dataset.textDesign; this.changed(); }
        if (button.dataset.textSize) { this.state.textSize = Number(button.dataset.textSize); this.changed(); }
        if (button.dataset.textPosition) { this.state.textPosition = button.dataset.textPosition; this.changed(); }
        if (button.hasAttribute('data-reset')) { this.state.zoom = 1; this.state.fx = this.state.fy = .5; this.changed(true); }
      });
      this.listen(this.zoom, 'input', () => { this.state.zoom = Number(this.zoom.value); this.changed(true, true); });
      this.listen(this.zoom, 'change', () => this.scheduleRender(false));
      this.listen(this.textarea, 'input', () => { this.state.text = this.textarea.value.slice(0, 60); this.changed(); this.renderTextCards(); });
      this.listen(this.scope, 'change', event => {
        if (event.target.name?.startsWith('options[') || event.target.name === 'id') requestAnimationFrame(() => this.updateVariant());
      }, {capture: true});
      // Stiletto chips can change selection before a native change event fires.
      this.listen(this.scope, 'click', event => {
        if (event.target.closest('[data-option-buttons] [data-button]')) requestAnimationFrame(() => this.updateVariant());
      }, {capture: true});
      this.listen(this.form, 'click', event => { if (event.target.closest('[data-add-to-cart]')) event.stopImmediatePropagation(); }, {capture: true});
      this.listen(this.form, 'submit', event => { event.preventDefault(); event.stopImmediatePropagation(); this.submit(); }, {capture: true});
      this.listen(this.preview, 'pointerdown', event => {
        if ((!this.source && !this.demoSource) || this.busy || event.isPrimary === false || event.button > 0) return;
        event.preventDefault(); this.preview.setPointerCapture(event.pointerId);
        const crop = this.crop(); this.drag = {id: event.pointerId, x: event.clientX, y: event.clientY, fx: crop.fx, fy: crop.fy, crop};
      });
      this.listen(this.preview, 'pointermove', event => {
        if (!this.drag || this.drag.id !== event.pointerId) return;
        const box = this.preview.getBoundingClientRect(), d = this.drag;
        const src = this.source || this.demoSource;
        this.state.fx = d.fx - (event.clientX - d.x) / box.width * d.crop.width / src.width;
        this.state.fy = d.fy - (event.clientY - d.y) / box.height * d.crop.height / src.height;
        const crop = this.crop(); this.state.fx = crop.fx; this.state.fy = crop.fy;
        this.changed(true, true);
      });
      for (const name of ['pointerup', 'pointercancel', 'lostpointercapture']) this.listen(this.preview, name, () => { if (this.drag) { this.drag = null; this.scheduleRender(); this.queueThumbnails(); } });
      this.listen(this.preview, 'keydown', event => {
        if (this.busy || (!this.source && !this.demoSource) || !['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home'].includes(event.key)) return;
        event.preventDefault();
        if (event.key === 'Home') { this.state.zoom = 1; this.state.fx = this.state.fy = .5; }
        else {
          const c = this.crop(), src = this.source || this.demoSource, step = event.shiftKey ? .08 : .025;
          if (event.key === 'ArrowLeft') this.state.fx += c.width / src.width * step;
          if (event.key === 'ArrowRight') this.state.fx -= c.width / src.width * step;
          if (event.key === 'ArrowUp') this.state.fy += c.height / src.height * step;
          if (event.key === 'ArrowDown') this.state.fy -= c.height / src.height * step;
        }
        const c = this.crop(); this.state.fx = c.fx; this.state.fy = c.fy; this.changed(true);
      });
    }
    suppressLegacy() {
      for (const node of this.scope.querySelectorAll('[data-ph-fieldname], [class*="uploadly"], [id*="uploadly"], [role~="uploadcare-uploader"], .uploadcare--widget')) {
        if (this.contains(node) || node.contains(this) || node.classList.contains('kadp__legacy')) continue;
        node.classList.add('kadp__legacy'); node.setAttribute('aria-hidden', 'true');
        if ('disabled' in node) node.disabled = true;
        for (const input of node.querySelectorAll('input,select,textarea,button')) { input.disabled = true; input.removeAttribute('required'); }
      }
    }
    readVariant() {
      const selected = new Map();
      for (const input of this.scope.querySelectorAll('input,select')) {
        if (!input.name.startsWith('options[') || (['radio', 'checkbox'].includes(input.type) && !input.checked)) continue;
        selected.set(input.name.slice(8, -1), input.value);
      }
      const optionNames = this.config.options.map(v => typeof v === 'string' ? v : v.name);
      const match = this.config.variants.find(v => v.options.every((value, i) => selected.get(optionNames[i]) === value));
      if (selected.size && match) return match;
      const id = this.form.querySelector('[name="id"]')?.value || this.config.variantId;
      return this.config.variants.find(v => String(v.id) === String(id)) || this.config.variants[0];
    }
    updateVariant() {
      const next = this.readVariant(), previous = this.variant;
      this.variant = next;
      if (next) {
        const variantField = this.form.querySelector('select[name="id"]');
        if (variantField && variantField.value !== String(next.id)) variantField.value = String(next.id);
        const priceDisplay = this.scope.querySelector('.product__price [data-price]');
        if (priceDisplay && next.priceText) priceDisplay.textContent = next.priceText;
        const personalizerPrice = this.querySelector('[data-kadp-price]');
        if (personalizerPrice && next.priceText) personalizerPrice.textContent = next.priceText;
      }
      const size = next?.title.match(/(\d+(?:[.,]\d+)?)\s*[×xX✕]\s*(\d+(?:[.,]\d+)?)/);
      const ratio = size ? Number(size[1].replace(',', '.')) / Number(size[2].replace(',', '.')) : 1.5;
      const ratioChanged = this.ratio !== ratio;
      this.ratio = ratio;
      this.querySelector('[data-format]').textContent = next?.title || '–';
      this.querySelector('[data-board]').style.aspectRatio = String(ratio);
      if (previous && previous.id !== next?.id) { this.pending = null; this.revision++; this.save(); }
      if (ratioChanged) { this.processedKey = ''; this.scheduleRender(); this.queueThumbnails(); }
      this.updateButtons(); this.updateSummary();
    }
    updateButtons() {
      for (const button of this.buttons) {
        button.disabled = Boolean(this.busy || this.loading || !this.file || !this.variant?.available);
        button.setAttribute('aria-disabled', String(button.disabled));
        if (this.busy) button.setAttribute('aria-busy', 'true'); else button.removeAttribute('aria-busy');
      }
    }
    syncControls() {
      this.zoom.value = this.state.zoom; this.textarea.value = this.state.text;
      this.querySelector('[data-zoom-output]').value = this.state.zoom.toFixed(1).replace('.', ',') + '×';
      this.querySelector('[data-count]').textContent = this.state.text.length + ' / 60';
      for (const [attribute, value] of [['data-style', this.state.style], ['data-text-design', this.state.textDesign], ['data-text-size', this.state.textSize], ['data-text-position', this.state.textPosition]]) {
        for (const button of this.querySelectorAll('button[' + attribute + ']')) button.setAttribute('aria-pressed', String(button.getAttribute(attribute) === String(value)));
      }
      this.querySelector('[data-style-help]').textContent = this.state.style === 'cloud' ? this.t.cloud_help : this.t.style_help;
      this.updateSummary();
    }
    updateSummary() {
      this.querySelector('[data-summary]').textContent = this.file ? [this.variant?.title, this.t['style_' + this.state.style], this.state.text.trim() ? this.t['text_' + this.state.textDesign] + ' · ' + this.t['position_' + this.state.textPosition] : ''].filter(Boolean).join(' · ') : this.t.review_empty;
    }
    changed(cropChanged = false, fast = false) {
      this.revision++; this.pending = null; this.syncControls(); this.save(); this.scheduleRender(fast);
      if (cropChanged) this.queueThumbnails();
      this.showError('');
    }
    save() {
      clearTimeout(this.saveTimer);
      this.saveTimer = setTimeout(() => {
        try { sessionStorage.setItem(this.storageKey, JSON.stringify({state: this.state, pending: this.pending || null})); } catch {}
      }, 180);
    }
    async loadFile(file, restored) {
      const version = ++this.loadVersion;
      this.loading = true; this.updateButtons(); this.status.textContent = this.t.processing; this.showError('');
      let objectUrl;
      try {
        if (!/\.(jpe?g|png|webp)$/i.test(file.name)) throw new Error(this.t.invalid_type);
        if (file.size > 20 * 1024 * 1024) throw new Error(this.t.too_large);
        const head = new Uint8Array(await file.slice(0, 16).arrayBuffer());
        const jpeg = head[0] === 255 && head[1] === 216 && head[2] === 255;
        const png = head[0] === 137 && head[1] === 80 && head[2] === 78 && head[3] === 71;
        const webp = String.fromCharCode(...head.slice(0, 4)) === 'RIFF' && String.fromCharCode(...head.slice(8, 12)) === 'WEBP';
        if (!jpeg && !png && !webp) throw new Error(this.t.invalid_type);
        objectUrl = URL.createObjectURL(file);
        const image = new Image(); image.src = objectUrl;
        if (image.decode) await image.decode(); else await new Promise((resolve, reject) => { image.onload = resolve; image.onerror = reject; });
        if (version !== this.loadVersion) return;
        if (!image.naturalWidth || !image.naturalHeight) throw new Error(this.t.invalid_image);
        const scale = Math.min(1, 1800 / Math.max(image.naturalWidth, image.naturalHeight));
        const source = document.createElement('canvas'); source.width = Math.max(1, Math.round(image.naturalWidth * scale)); source.height = Math.max(1, Math.round(image.naturalHeight * scale));
        const ctx = source.getContext('2d', {willReadFrequently: true}); ctx.fillStyle = '#fff'; ctx.fillRect(0, 0, source.width, source.height);
        ctx.drawImage(image, 0, 0, source.width, source.height);
        this.sourceInfo = {width: image.naturalWidth, height: image.naturalHeight, name: file.name, bytes: file.size};
        image.src = ''; this.source = source; this.file = file; this.fileVersion++; this.processedKey = '';
        if (!restored) { this.state.zoom = 1; this.state.fx = this.state.fy = .5; this.pending = null; }
        this.classList.add('kadp--ready'); this.dataset.ready = 'true';
        const photo = this.querySelector('[data-demo-photo]');
      if (photo) photo.hidden = true;
      const label = this.querySelector('[data-demo-label]');
      if (label) label.hidden = true;
        this.querySelector('[data-filename]').textContent = file.name;
        this.querySelector('[data-upload-label]').textContent = this.t.replace_button;
        this.status.textContent = (restored ? this.t.restored : this.t.ready) + (Math.max(image.naturalWidth, image.naturalHeight) < 900 ? ' ' + this.t.low_resolution : '');
        this.syncControls(); this.save(); this.renderMain(); this.queueThumbnails();
        if (!restored) storeFile(this.fileKey, file).catch(() => {});
      } catch (error) {
        if (version === this.loadVersion) { this.showError([this.t.invalid_type, this.t.too_large, this.t.invalid_image].includes(error?.message) ? error.message : this.t.invalid_image); this.input.value = ''; this.status.textContent = this.file ? this.t.ready : ''; }
      } finally {
        if (objectUrl) URL.revokeObjectURL(objectUrl);
        if (version === this.loadVersion) { this.loading = false; this.updateButtons(); }
      }
    }
    loadDemo() {
      const url = this.dataset.demoImage;
      if (!url) return;
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        if (!this.isConnected || this.file) return;
        try {
          const scale = Math.min(1, 1100 / Math.max(img.naturalWidth, img.naturalHeight));
          const c = document.createElement('canvas');
          c.width = Math.max(1, Math.round(img.naturalWidth * scale));
          c.height = Math.max(1, Math.round(img.naturalHeight * scale));
          const context = c.getContext('2d', {willReadFrequently: true});
          context.drawImage(img, 0, 0, c.width, c.height);
          context.getImageData(0, 0, 1, 1);
          this.demoSource = c;
          this.processedKey = '';
          this.renderMain();
          this.queueThumbnails();
          const photo = this.querySelector('[data-demo-photo]');
          if (photo) photo.hidden = true;
        } catch { this.demoSource = null; }
      };
      img.src = url;
    }
    crop() { const src = this.source || this.demoSource; return E.cropRect(src.width, src.height, this.ratio, this.state.zoom, this.state.fx, this.state.fy); }
    cropped(longSide) {
      const crop = this.crop(), c = document.createElement('canvas');
      c.width = this.ratio >= 1 ? longSide : Math.round(longSide * this.ratio); c.height = Math.round(c.width / this.ratio);
      const ctx = c.getContext('2d', {willReadFrequently: true}); ctx.fillStyle = '#fff'; ctx.fillRect(0, 0, c.width, c.height);
      ctx.drawImage(this.source || this.demoSource, crop.x, crop.y, crop.width, crop.height, 0, 0, c.width, c.height);
      return c;
    }
    renderBlank() {
      const c = E.board(this.preview.width, this.preview.height); this.preview.getContext('2d').drawImage(c, 0, 0);
      for (const button of this.querySelectorAll('[data-style]')) { const c = button.querySelector('canvas'); c.getContext('2d').drawImage(E.board(c.width, c.height), 0, 0); }
    }
    scheduleRender(fast = false) {
      cancelAnimationFrame(this.renderFrame); clearTimeout(this.detailTimer);
      this.renderFrame = requestAnimationFrame(() => this.renderMain(fast));
      if (fast) this.detailTimer = setTimeout(() => this.scheduleRender(), 110);
    }
    renderMain(fast = false) {
      if (!this.source && !this.demoSource) return;
      const longSide = window.matchMedia('(max-width: 719px)').matches ? 720 : 960;
      const w = this.ratio >= 1 ? longSide : Math.round(longSide * this.ratio), h = Math.round(w / this.ratio);
      if (this.preview.width !== w || this.preview.height !== h) { this.preview.width = w; this.preview.height = h; this.wood = null; }
      if (!this.wood) this.wood = E.board(w, h);
      const key = [this.fileVersion, this.ratio, this.state.style, this.state.zoom, this.state.fx, this.state.fy, fast].join(':');
      if (this.processedKey !== key) { this.processed = E.process(this.cropped(fast ? Math.min(480, longSide) : longSide), this.state.style); this.processedKey = key; }
      this.textLayout = E.compose(this.preview, this.processed, this.state, this.wood);
      this.preview.dataset.style = this.state.style; this.preview.dataset.renderVersion = String(this.revision);
    }
    queueThumbnails() {
      clearTimeout(this.thumbnailTimer);
      const version = (this.thumbnailVersion || 0) + 1; this.thumbnailVersion = version;
      this.thumbnailTimer = setTimeout(async () => {
        if ((!this.source && !this.demoSource) || !this.isConnected) return;
        const source = this.cropped(210);
        for (const button of this.querySelectorAll('button[data-style]')) {
          if (version !== this.thumbnailVersion || !this.isConnected) return;
          const canvas = button.querySelector('canvas'); canvas.width = source.width; canvas.height = source.height;
          E.compose(canvas, E.process(source, button.dataset.style), {}, E.board(source.width, source.height));
          button.dataset.previewReady = 'true';
          await new Promise(requestAnimationFrame);
        }
      }, 200);
    }
    renderTextCards() { /* Two simple lettering buttons; no thumbnail cards. */ }
    showError(message) { this.errorBox.textContent = message; this.errorBox.hidden = !message; }
    layout(variant) {
      const crop = this.crop();
      return {v: 1, product: this.dataset.productId, variant: String(variant.id), format: variant.title, source: this.sourceInfo,
        crop: {x: round(crop.x / this.source.width), y: round(crop.y / this.source.height), width: round(crop.width / this.source.width), height: round(crop.height / this.source.height), zoom: this.state.zoom, fx: round(crop.fx), fy: round(crop.fy)},
        style: this.state.style, text: this.state.text.trim(), textDesign: this.state.textDesign, textSize: this.state.textSize, textPosition: this.state.textPosition,
        preview: {width: this.preview.width, height: this.preview.height, textLines: this.textLayout?.lines, textFontSize: this.textLayout?.fontSize}};
    }
    hostedFile(value) {
      if (typeof value !== 'string') return false;
      try { const u = new URL(value, location.origin); return u.protocol === 'https:' && (u.hostname === 'cdn.shopify.com' || u.hostname === location.hostname) && /\/uploads\//.test(u.pathname); } catch { return false; }
    }
    validLine(line, pending) {
      const p = line?.properties;
      return p?._KAD_Design === pending.id && p?._KAD_Layout === pending.layout && this.hostedFile(p.Originalfoto) && this.hostedFile(p.Gestaltungsvorschau);
    }
    async cart() {
      const response = await fetch(this.dataset.cartUrl + '.js', {credentials: 'same-origin', cache: 'no-store', headers: {Accept: 'application/json'}});
      if (!response.ok) throw new Error('Cart unavailable');
      return response.json();
    }
    async confirmUploadedLine() {
      for (let attempt = 0; attempt < 4; attempt++) {
        const line = (await this.cart()).items.find(item => item.properties?._KAD_Design === this.pending?.id);
        if (this.validLine(line, this.pending)) return line;
        if (attempt < 3) await new Promise(resolve => setTimeout(resolve, 350 * (attempt + 1)));
      }
      return null;
    }
    async finish() {
      clearTimeout(this.saveTimer);
      try { sessionStorage.removeItem(this.storageKey); } catch {}
      await Promise.race([storeFile(this.fileKey, null).catch(() => {}), new Promise(resolve => setTimeout(resolve, 500))]);
      location.assign(this.dataset.cartUrl);
    }
    async removeInvalid(line) {
      if (!line?.key || !this.pending || line.properties?._KAD_Design !== this.pending.id) return;
      const response = await fetch(this.dataset.cartUrl + '/change.js', {method: 'POST', credentials: 'same-origin', headers: {'Content-Type': 'application/json'}, body: JSON.stringify({id: line.key, quantity: 0})});
      if (!response.ok) throw new Error('Cart verification failed');
    }
    // Shopify's native multipart product form owns file uploads, not Ajax cart/add.js.
    // This preserves both the original photograph and the processed design.
    setFileInput(input, file) {
      if (!window.DataTransfer) throw new Error(this.t.upload_error);
      const transfer = new DataTransfer();
      transfer.items.add(file);
      input.files = transfer.files;
      if (input.files.length !== 1 || input.files[0].size !== file.size) throw new Error(this.t.upload_error);
    }
    setProperty(name, value) {
      const existing = [...this.form.querySelectorAll('input[data-kadp-property]')]
        .find(input => input.name === name);
      const field = existing || document.createElement('input');
      field.type = 'hidden';
      field.name = name;
      field.value = value;
      field.dataset.kadpProperty = 'true';
      if (!existing) this.form.appendChild(field);
    }
    async submit() {
      if (this.busy || this.loading) return;
      this.updateVariant();
      if (!this.file || !this.source) {
        this.showError(this.t.upload_first);
        this.input.focus();
        return;
      }
      const variant = this.readVariant();
      if (!variant?.available) { this.showError(this.t.cart_error); return; }
      this.busy = true;
      this.classList.add('kadp__busy');
      this.updateButtons();
      this.status.textContent = this.t.uploading;
      this.showError('');
      try {
        if (document.fonts) await document.fonts.ready;
        cancelAnimationFrame(this.renderFrame);
        this.renderMain();
        const blob = await new Promise(resolve => this.preview.toBlob(resolve, 'image/png'));
        if (!blob) throw new Error(this.t.upload_error);
        const design = new File([blob], 'KozArtDesign-Vorschau.png', {type:'image/png'});
        // Keep the internal order property compact. The preview PNG is the visual source of truth.
        const layout = JSON.stringify({
          v: 2, style: this.state.style,
          zoom: round(this.state.zoom), fx: round(this.state.fx), fy: round(this.state.fy),
          textDesign: this.state.textDesign, textSize: this.state.textSize,
          textPosition: this.state.textPosition
        });

        // Files attached with form= are sent even though the editor lives outside the form.
        this.setFileInput(this.input, this.file);
        let previewField = this.form.querySelector('input[data-kadp-preview-file]');
        if (!previewField) {
          previewField = document.createElement('input');
          previewField.type = 'file';
          previewField.name = 'properties[Gestaltungsvorschau]';
          previewField.hidden = true;
          previewField.setAttribute('data-kadp-preview-file', '');
          this.form.appendChild(previewField);
        }
        this.setFileInput(previewField, design);
        const variantSelect = this.form.querySelector('select[name="id"]');
        if (!variantSelect) throw new Error(this.t.cart_error);
        variantSelect.value = String(variant.id);
        if (variantSelect.value !== String(variant.id)) throw new Error(this.t.cart_error);

        this.setProperty('properties[Bildstil]', this.t['style_' + this.state.style]);
        this.setProperty('properties[Format]', variant.title);
        this.setProperty('properties[_KAD_Layout]', layout);
        this.setProperty('properties[_KAD_Version]', '2.0');
        if (this.state.text.trim()) {
          this.setProperty('properties[Persönlicher Text]', this.state.text.trim());
          this.setProperty('properties[Textdesign]', this.t['text_' + this.state.textDesign]);
          this.setProperty('properties[Textposition]', this.t['position_' + this.state.textPosition]);
          this.setProperty('properties[Textgröße]', this.t[this.state.textSize === .8 ? 'small' : this.state.textSize === 1.2 ? 'large' : 'medium']);
        } else {
          for (const name of ['properties[Persönlicher Text]', 'properties[Textdesign]', 'properties[Textposition]', 'properties[Textgröße]']) {
            this.form.querySelectorAll('input[data-kadp-property]').forEach(field => {
              if (field.name === name) field.remove();
            });
          }
        }
        this.form.enctype = 'multipart/form-data';
        this.form.method = 'post';
        // Bypass Stiletto's Ajax submit interceptor; allow Shopify to receive real files.
        HTMLFormElement.prototype.submit.call(this.form);
      } catch (error) {
        this.showError(error?.message === this.t.cart_error ? this.t.cart_error : this.t.upload_error);
        this.status.textContent = this.t.ready;
        this.busy = false;
        this.classList.remove('kadp__busy');
        this.updateButtons();
      }
    }
  }
  customElements.define('kozart-personalizer', KozartPersonalizer);
})();
